/**
 * Schema.org Organization JSON-LD — establishes tenant identity for
 * Google's Knowledge Graph and AI search grounding.
 *
 * <p>Emit once per tenant on the home/landing page. Per-page Course /
 * Video / Article schemas reference this identity via their own
 * {@code publisher: { '@type': 'Organization', ... }} field — emitting
 * the Organization once at the site level anchors that identity.
 */
import { JsonLdScript } from './_shared';

export interface OrganizationSchemaJsonLdProps {
  name: string;
  url: string;
  /** Logo image URL → ImageObject (required for Google's "Logo" rich
   *  result eligibility). */
  logoUrl?: string;
  alternateName?: string;
  description?: string;
  /** External profile URLs (LinkedIn, X, YouTube, Instagram, Wikipedia).
   *  Strongest single signal for Knowledge Graph linking. */
  sameAs?: string[];
  contactPoint?: {
    contactType: string;
    email?: string;
    telephone?: string;
    areaServed?: string;
    availableLanguage?: string[];
  };
  id?: string;
}

export function OrganizationSchemaJsonLd({ id, ...input }: OrganizationSchemaJsonLdProps) {
  const schema = buildOrganizationSchema(input);
  return <JsonLdScript schema={schema} id={id ?? 'schema-organization'} />;
}

export function buildOrganizationSchema(input: Omit<OrganizationSchemaJsonLdProps, 'id'>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: input.name,
    url: input.url,
    alternateName: input.alternateName,
    description: input.description,
    logo: input.logoUrl
      ? {
          '@type': 'ImageObject',
          url: input.logoUrl,
        }
      : undefined,
    sameAs: input.sameAs && input.sameAs.length > 0 ? input.sameAs : undefined,
    contactPoint: input.contactPoint
      ? {
          '@type': 'ContactPoint',
          contactType: input.contactPoint.contactType,
          email: input.contactPoint.email,
          telephone: input.contactPoint.telephone,
          areaServed: input.contactPoint.areaServed,
          availableLanguage: input.contactPoint.availableLanguage,
        }
      : undefined,
  };
}
